const DB_NAME = "cs_winner_select_db_v1";
const DB_VERSION = 1;
const STORE_NAME = "countdown_payloads";
const MAX_RECORD_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_RECORD_COUNT = 25;

function supportsIndexedDb() {
  return typeof window !== "undefined" && typeof window.indexedDB !== "undefined";
}

function openCountdownDb() {
  return new Promise((resolve, reject) => {
    if (!supportsIndexedDb()) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }

    const req = window.indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Failed to open IndexedDB"));
  });
}

function createDrawId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

async function writeRecord(record) {
  const db = await openCountdownDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error("IndexedDB write failed"));
      tx.onabort = () => reject(tx.error || new Error("IndexedDB write aborted"));
    });
  } finally {
    db.close();
  }
}

async function readRecord(drawId) {
  const db = await openCountdownDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(drawId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error || new Error("IndexedDB read failed"));
      tx.onabort = () => reject(tx.error || new Error("IndexedDB read aborted"));
    });
  } finally {
    db.close();
  }
}

async function pruneStore() {
  if (!supportsIndexedDb()) return;

  const db = await openCountdownDb();
  const cutoff = Date.now() - MAX_RECORD_AGE_MS;

  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const allReq = store.getAll();

      allReq.onsuccess = () => {
        const all = Array.isArray(allReq.result) ? allReq.result : [];

        all
          .filter((r) => Number(r?.createdAt || 0) < cutoff)
          .forEach((r) => store.delete(r.id));

        const survivors = all
          .filter((r) => Number(r?.createdAt || 0) >= cutoff)
          .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));

        survivors.slice(MAX_RECORD_COUNT).forEach((r) => store.delete(r.id));
      };

      allReq.onerror = () =>
        reject(allReq.error || new Error("IndexedDB prune read failed"));

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error("IndexedDB prune failed"));
      tx.onabort = () => reject(tx.error || new Error("IndexedDB prune aborted"));
    });
  } finally {
    db.close();
  }
}

export async function saveCountdownPayloadToStore(payload) {
  const id = createDrawId();
  const createdAt = Date.now();
  const record = { id, createdAt, payload };

  await writeRecord(record);
  void pruneStore().catch(() => {});

  return {
    storage: "idb",
    drawId: id,
    createdAt,
  };
}

export async function loadCountdownPayloadFromStore(drawId) {
  if (!drawId) return null;
  if (!supportsIndexedDb()) return null;

  const record = await readRecord(drawId);
  if (!record?.payload) return null;

  const createdAt = Number(record.createdAt || 0);
  if (createdAt && Date.now() - createdAt > MAX_RECORD_AGE_MS) {
    return null;
  }

  return record.payload;
}
